import React, {ChangeEvent, ReactNode} from 'react'
import {DateFormatter, DayPicker, Matcher} from 'react-day-picker'
import 'react-day-picker/dist/style.css'
import {Nullable, Optional} from '../../../../app/TypeHelpers'
import moment from 'moment'
import {Popover} from '@mui/material'
import 'moment/locale/ja'
import {DisplayItemSelectLikeDiv, DisplayItemSelectWrapper} from '../../../../styledComponents/DisplayItem'
import styled from 'styled-components'
import {createErrorHandlingForm, ErrorHandlingFormProps} from '../hoc/createResidentErrorHandlingForm'
import {ErrorProps} from './ResidentHocInputErrorHandling'
import {formatForDateInput, formatForDateInputFromDate} from '../../../../formatters/formatDate'
import {format} from 'date-fns'
import {ja} from 'date-fns/locale'
import {Colors} from '../../../../styledComponents/Constants'

/***
 * パラメータ todayについては
 * maxDateを指定した場合に過去判定で使用するため、必ず値を指定する
 * 指定しない場合はデフォルトの表示月を当月とする
 ***/
export interface DatePickerProps {
    date?: Nullable<string>
    onChange?: (date: string) => void
    disabled?: boolean
    itemValue?: string
    minDate?: Nullable<Date>
    maxDate?: Nullable<Date>
    today?: Date
    holidays: string[]
}

interface State {
    anchorEL: Nullable<HTMLElement>
    selectedMoment: Nullable<moment.Moment>
}

const formatCaption: DateFormatter = (date, options) => {
    const y = new Date(date.toLocaleString('ja')).getFullYear();
    const m = format(date, 'MM', { locale: ja });
    return `${y}年 ${Number(m)}月`;
}

class PPDatePickerForResident
    extends React.Component<DatePickerProps & ErrorHandlingFormProps & ErrorProps, State> {

    constructor(props: Readonly<DatePickerProps & ErrorHandlingFormProps & ErrorProps>) {
        super(props)
        this.state = {
            anchorEL: null,
            selectedMoment: null,
        }
        this.handleOnDayClick = this.handleOnDayClick.bind(this)
        this.handleChangeDate = this.handleChangeDate.bind(this)
    }

    componentDidMount(): void {
        this.updateStateDate()
    }

    componentDidUpdate(prevProps: Readonly<DatePickerProps>, prevState: Readonly<State>, snapshot?: any): void {
        const thisPropsMoment = (this.props.date) ? moment(this.props.date) : null
        if (prevProps.date === this.props.date || this.state.selectedMoment === thisPropsMoment) {
            return
        }
        this.updateStateDate()
    }

    updateStateDate(): void {
        this.setState({
            selectedMoment: this.props.date ? moment(this.props.date) : null,
        })
    }

    handleOnDayClick(day: Date): void {
        const dateOnlyFormat = formatForDateInput(day.toString())
        if (this.props.minDate && (dateOnlyFormat < formatForDateInput(this.props.minDate.toString()))) {
            return
        }
        if (this.props.maxDate && (dateOnlyFormat > formatForDateInput(this.props.maxDate.toString()))) {
            return
        }
        this.setState({
            anchorEL: null,
            selectedMoment: moment(day),
        }, () => {
            const {selectedMoment} = this.state
            if (this.props.onChange) {
                this.props.onChange(
                    (selectedMoment !== null)
                        ? selectedMoment.format('YYYY-MM-DD')
                        : '',
                )
            }
        })
    }

    handleChangeDate(event: ChangeEvent<HTMLInputElement>): void {
        this.handleOnDayClick(moment(event.target.value).toDate())
    }

    getInitialDate(): Optional<Date> {
        if (this.props.minDate) {
            return this.props.minDate
        }

        /*
         * maxDate指定時に、現在日時（today）が未指定の場合は、
         * 過去判定ができない為、undefinedを固定で返す
         */
        if (this.props.maxDate && !this.props.today) {
            return undefined
        }

        /*
        * 終了日が入っている場合は、呼び出し元から渡された今日日付(today)と比較し
        * 終了日が過去であれば終了日を返す
        */
        if (this.props.maxDate && this.props.today &&
            formatForDateInputFromDate(this.props.maxDate) < formatForDateInputFromDate(this.props.today)) {
            return this.props.maxDate
        }

        return undefined
    }

    render(): ReactNode {
        const {selectedMoment, anchorEL} = this.state
        const {minDate, maxDate} = this.props

        const itemValue: string = (selectedMoment !== null)
            ? selectedMoment.format('YYYY年M月D日(ddd)')
            : (this.props.itemValue !== undefined)
                ? this.props.itemValue
                : '日付を選択してください'

        let hasError: boolean = false
        if (this.props.hasError) {
            hasError = this.props.hasError
        }
        const holidayList = this.props.holidays.map(holiday => new Date(holiday))
        const modifiers = {
            sun: {dayOfWeek: [0]},
            sat: {dayOfWeek: [6]},
            holidays: holidayList
        }
        const modifiersStyles = {
            sun: {color: '#ff0000'},
            sat: {color: '#0000ff'},
            holidays: {color: '#ff0000'},
        }

        const disabledDays: Matcher[] = []
        if (minDate) {
            disabledDays.push({before: minDate})
        }

        if (maxDate) {
            disabledDays.push({after: maxDate})
        }

        const month = (selectedMoment !== null)
             ? selectedMoment.toDate()
             : this.getInitialDate()

        return <>
            <div
                onClick={(event: React.MouseEvent<HTMLElement>) => {
                    if (this.props.disabled) {
                        return
                    }
                    event.preventDefault()
                    this.setState({
                        anchorEL: event.currentTarget,
                    })
                }}>
                <DisplayItemSelectWrapper>
                    <DisplayItemSelectLikeDiv hasError={hasError}
                                              disabled={this.props.disabled}>{itemValue}</DisplayItemSelectLikeDiv>
                </DisplayItemSelectWrapper>
            </div>
            <input
                style={{display: 'none'}}
                type={'date'}
                value={this.props.date || ''}
                onChange={this.handleChangeDate}/>
            <Popover
                open={(anchorEL !== null)}
                anchorEl={anchorEL}
                onClose={() => {
                    this.setState({anchorEL: null})
                }}
            >
                <WrappedDayPicker
                    modifiers={modifiers}
                    modifiersStyles={modifiersStyles}
                    disabled={disabledDays.length ? disabledDays : undefined}
                    fromMonth={(minDate)
                        ? minDate
                        : undefined
                    }
                    toMonth={(maxDate)
                        ? maxDate
                        : undefined
                    }
                    selected={
                        (selectedMoment !== null)
                            ? [selectedMoment.toDate()]
                            : undefined
                    }
                    defaultMonth={month}
                    locale={ja}
                    formatters={{ formatCaption }}
                    onDayClick={this.handleOnDayClick}
                />
            </Popover>
        </>
    }
}

export default createErrorHandlingForm(PPDatePickerForResident)


const WrappedDayPicker = styled(DayPicker)`
  word-break: normal;
  line-height: 1.15;
  .rdp-day_today {
    color: #3f51b5;
    font-weight: normal;
   }
  .rdp-day_disabled {
    color: ${Colors.Body}!important;
   }
`
